/**
 * Consolidated formatting/normalization helpers — previously duplicated across
 * OrderPage.js, ProductCard.js, ProductDetailModal.js and CartDrawer.js.
 * Pure functions only; no React, no API calls.
 */

export function optimizeCloudinaryUrl(url, options = {}) {
  if (!url || typeof url !== 'string' || url.trim() === '') return null;
  if (!url.includes('res.cloudinary.com')) return url;

  const {
    width = 500,
    height,
    quality = 'auto',
    format = 'auto',
  } = options;

  const match = url.match(/^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.*)$/i);
  if (!match) return url;

  const [, prefix, rest] = match;
  const hasQuery = rest.includes('?');
  const pathname = hasQuery ? rest.slice(0, rest.indexOf('?')) : rest;
  const query = hasQuery ? rest.slice(rest.indexOf('?')) : '';
  const segments = pathname.split('/');

  if (segments.some((segment) => /^f_|^q_|^w_|^h_|^c_|^g_/.test(segment))) {
    return url;
  }

  const normalizedWidth = Number(width) > 0 ? Math.round(Number(width)) : 500;
  const normalizedHeight = Number(height) > 0 ? Math.round(Number(height)) : null;
  const transformParts = [`f_${format}`, `q_${quality}`, `w_${normalizedWidth}`];
  if (normalizedHeight) transformParts.push(`h_${normalizedHeight}`);

  segments.unshift(transformParts.join(','));
  return `${prefix}${segments.join('/')}${query}`;
}

export function fixImageUrl(url, options = {}) {
  if (!url || typeof url !== 'string' || url.trim() === '') return null;
  let id = null;
  let m = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (m) id = m[1];
  if (!id) { m = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/); if (m) id = m[1]; }
  if (!id) { m = url.match(/drive\.google\.com\/uc\?[^\"]*id=([a-zA-Z0-9_-]+)/); if (m) id = m[1]; }
  if (!id) { m = url.match(/[?&]id=([a-zA-Z0-9_-]+)/); if (m && url.includes('google')) id = m[1]; }
  if (id) return `https://drive.google.com/thumbnail?id=${id}&sz=w400`;
  return optimizeCloudinaryUrl(url, options);
}

export const PLACEHOLDER_IMG = '/product-image-update-needed.png';

export function formatINR(n) {
  const v = Number(n) || 0;
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

/** Per-unit stepper config: presets (ProductCard), step/min (all), display label. */
export const UNIT_CONFIG = {
  kg:     { presets: [0.5, 1, 2],     step: 0.5, min: 0.5, label: 'kg',    inputHint: 'kg' },
  g:      { presets: [100, 250, 500], step: 50,  min: 50,  label: 'g',     inputHint: 'g' },
  litre:  { presets: [0.5, 1, 2],     step: 0.5, min: 0.5, label: 'ltr',   inputHint: 'litres' },
  l:      { presets: [0.5, 1, 2],     step: 0.5, min: 0.5, label: 'L',     inputHint: 'litres' },
  ml:     { presets: [100, 250, 500], step: 100, min: 100, label: 'ml',    inputHint: 'ml' },
  pcs:    { presets: [1, 2, 6],       step: 1,   min: 1,   label: 'pcs',   inputHint: 'pcs' },
  pc:     { presets: [1, 2, 6],       step: 1,   min: 1,   label: 'pcs',   inputHint: 'pcs' },
  count:  { presets: [6, 12, 24],     step: 1,   min: 1,   label: 'pcs',   inputHint: 'count' },
  pack:   { presets: [1, 2, 3],       step: 1,   min: 1,   label: 'packs', inputHint: 'packs' },
  bundle: { presets: [1, 2, 3],       step: 1,   min: 1,   label: 'bndl',  inputHint: 'bundles' },
  dozen:  { presets: [1, 2, 3],       step: 1,   min: 1,   label: 'dzn',   inputHint: 'dozens' },
};
export const DEFAULT_UNIT = UNIT_CONFIG.pcs;

export function unitConfigFor(product) {
  const key = (product?.unit || 'pcs').toLowerCase();
  const base = UNIT_CONFIG[key] || DEFAULT_UNIT;
  return {
    ...base,
    step: product?.unit_step != null ? Number(product.unit_step) : base.step,
    min: product?.min_qty != null ? Number(product.min_qty) : base.min,
  };
}

/** Cart-line quantity display, e.g. 0.25 kg -> "250g", 1.5 litre -> "1.5 L". */
export function formatUnit(qty, unit) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n < 0) return '0';
  const key = String(unit || 'pcs').toLowerCase();
  const trimNum = (v) => (v % 1 === 0 ? String(v) : String(+Number(v).toFixed(3)).replace(/\.?0+$/, ''));
  if (key === 'kg') {
    const g = Math.round(n * 1000);
    if (n > 0 && n < 1 && Math.abs(n * 1000 - g) < 0.51) return `${g}g`;
    return `${trimNum(n)} kg`;
  }
  if (key === 'litre' || key === 'l') {
    const ml = Math.round(n * 1000);
    if (n > 0 && n < 1 && Math.abs(n * 1000 - ml) < 0.51) return `${ml} ml`;
    return `${trimNum(n)} L`;
  }
  const label = (UNIT_CONFIG[key] && UNIT_CONFIG[key].label) || key;
  return `${trimNum(n)} ${label}`;
}

/** Sell price / MRP / discount %, resolved consistently across the app. */
export function priceInfo(product) {
  const sell = Number(product?.sell_price != null ? product.sell_price : product?.price_per_kg) || 0;
  const mrp = Number(product?.mrp != null ? product.mrp : sell) || sell;
  const off = product?.discount_percent != null
    ? Number(product.discount_percent)
    : (mrp > sell ? Math.round(((mrp - sell) / mrp) * 100) : 0);
  const saveAmt = Math.max(0, Math.round((mrp - sell) * 100) / 100);
  return { sell, mrp, off, saveAmt };
}

export function stockInfo(product) {
  const stockQty = Number(product?.stock_qty);
  const inStock = product?.in_stock !== false && (!Number.isFinite(stockQty) || stockQty > 0);
  const lowStock = Number.isFinite(stockQty) && stockQty > 0 && stockQty <= 5;
  return { inStock, lowStock, stockQty };
}

export function normalizeProduct(p, unitsConfig) {
  if (!p) return p;
  const toNum = (v, fb = null) => { const n = parseFloat(v); return Number.isFinite(n) ? n : fb; };
  const sell = toNum(p.sell_price, toNum(p.price_per_kg, 0));
  let mrp = toNum(p.mrp, null);
  if (mrp == null || mrp < sell) mrp = sell;
  const images = Array.isArray(p.images) && p.images.length ? p.images : (p.image_url ? [p.image_url] : []);
  const unitKey = (p.unit || 'pcs').toLowerCase();
  const u = (unitsConfig && unitsConfig[unitKey]) || { step: 1, min: 1 };
  const off = p.discount_percent != null ? parseInt(p.discount_percent, 10) : (mrp > sell ? Math.round(((mrp - sell) / mrp) * 100) : 0);
  return {
    ...p,
    mrp,
    sell_price: sell,
    price_per_kg: sell,
    discount_percent: off,
    images,
    image_url: images[0] || p.image_url || '',
    unit: unitKey,
    unit_step: toNum(p.unit_step, u.step),
    min_qty: toNum(p.min_qty, u.min),
    stock_qty: toNum(p.stock_qty, p.in_stock === false ? 0 : null),
  };
}

// ── Category tree helpers ──────────────────────────────────────
const CATEGORY_ALIASES = {
  home_care: 'cleaning',
  pooja_needs: 'pooja',
  packages: 'offers_deals_groceries',
};
const HIDDEN_BROWSE_CATEGORY_IDS = new Set(['leafy', 'leafy_premium', 'packages']);

export function isHiddenBrowseCategory(categoryId) {
  return HIDDEN_BROWSE_CATEGORY_IDS.has(String(categoryId || '').trim());
}

export function normalizeCategoryId(categoryId) {
  if (categoryId == null) return categoryId;
  const key = String(categoryId).trim();
  return CATEGORY_ALIASES[key] || key;
}

export function rootsInSection(rows, sectionId) {
  return (rows || [])
    .filter((c) => c.section_id === sectionId && !c.parent_id && !isHiddenBrowseCategory(c.id))
    .slice()
    .sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
}

export function childrenInSection(rows, parentId, sectionId) {
  if ((parentId || null) === 'vegetables') return [];
  return (rows || [])
    .filter((c) => (c.parent_id || null) === (parentId || null)
      && (!sectionId || c.section_id === sectionId)
      && !isHiddenBrowseCategory(c.id))
    .slice()
    .sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
}

export function collectDescendantIds(categoryId, rows) {
  const rootId = normalizeCategoryId(categoryId);
  const map = {};
  (rows || []).forEach((c) => {
    const p = c.parent_id || '';
    if (!map[p]) map[p] = [];
    map[p].push(c.id);
  });
  const out = new Set([rootId]);
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop();
    (map[id] || []).forEach((cid) => {
      if (!out.has(cid)) { out.add(cid); stack.push(cid); }
    });
  }
  return out;
}

export function storeTabsFromSections(sections) {
  const visible = (sections || []).filter((s) => s && s.show_on_home !== false);
  const primaryIds = ['groceries', 'food', 'bazaar'];
  const primary = primaryIds.map((id) => visible.find((s) => s.id === id)).filter(Boolean);
  if (primary.length) return primary;
  if (visible.length) return visible.slice().sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
  return [
    { id: 'groceries', name_en: 'Groceries', name_te: 'కిరాణా', emoji: '🛒' },
    { id: 'food', name_en: 'Food', name_te: 'ఫుడ్', emoji: '🍱' },
    { id: 'bazaar', name_en: 'Bazaar', name_te: 'బజార్', emoji: '🛍️' },
  ];
}

// ── Hero banner list — shows exactly what's configured in admin, in the
// order set there (offers.sort_order), no fixed slot count and no fabricated
// placeholder banners. Each banner is clickable via link_url (opens directly),
// else category_id (filters the product grid), else just shows a preview. ──
export function buildBannerList(offers = [], promos = []) {
  const fromOffers = (offers || [])
    .filter((o) => o && o.image_url)
    .map((o) => ({
      id: `offer-${o.id}`,
      title: o.title || '',
      description: o.description || '',
      image_url: o.image_url,
      link_url: o.link_url || null,
      category_id: o.category_id || null,
      promoCode: null,
      sort_order: Number.isFinite(Number(o.sort_order)) ? Number(o.sort_order) : 99,
    }));
  const fromPromos = (promos || [])
    .filter((p) => p && p.image_url)
    .map((p) => ({
      id: `promo-${p.id || p.code || p.title}`,
      title: p.description || p.code || '',
      description: p.description || '',
      image_url: p.image_url,
      link_url: null,
      category_id: null,
      promoCode: p.code || null,
      sort_order: 999,
    }));
  return [...fromOffers, ...fromPromos].sort((a, b) => a.sort_order - b.sort_order);
}

// ── Misc ────────────────────────────────────────────────────────
export function buildUpiPayUrl({ pa, pn, amount, note, tr }) {
  const params = new URLSearchParams();
  params.set('pa', String(pa || '').trim());
  params.set('pn', String(pn || 'NRK Mart').trim().slice(0, 100));
  params.set('am', Math.max(0, Number(amount || 0)).toFixed(2));
  params.set('cu', 'INR');
  if (note) params.set('tn', String(note).slice(0, 80));
  params.set('tr', String(tr || `NRK${Date.now()}`).replace(/\W/g, '').slice(0, 35));
  return `upi://pay?${params.toString()}`;
}

export function payMethodLabel(method) {
  const m = String(method || 'cod').toLowerCase();
  if (m === 'razorpay') return 'Razorpay (Online)';
  if (m === 'cashfree') return 'Cashfree (Online)';
  if (m === 'upi') return 'UPI';
  if (m === 'wallet') return 'Wallet';
  return 'Cash on Delivery';
}

export function isUnpaidOnline(order) {
  const method = String(order?.payment_method || '').toLowerCase();
  if (!['razorpay', 'cashfree', 'upi'].includes(method)) return false;
  return String(order?.payment_status || 'pending') !== 'verified';
}

/**
 * order.status is 'pending' the moment the order row is created, before
 * online payment is ever attempted — that's tracked separately via
 * isUnpaidOnline(). This picks the right STATUS_META key to *display* so a
 * genuinely-placed order (paid, or COD) reads differently from one still
 * awaiting an online payment, without changing the underlying status value.
 */
export function displayStatusKey(order) {
  if (order?.status === 'pending' && isUnpaidOnline(order)) return 'payment_pending';
  return order?.status || 'pending';
}

export function payStatusLabel(order) {
  if (isUnpaidOnline(order)) return 'Unpaid';
  const raw = String(order?.payment_status || 'verified').toLowerCase();
  if (raw === 'rejected' || raw === 'failed') return 'Failed';
  return 'Paid';
}

export function razorpayPayId(order) {
  if (String(order?.payment_method || '').toLowerCase() !== 'razorpay') return '';
  if (String(order?.payment_status || '') !== 'verified') return '';
  const gw = String(order?.gateway_payment_id || order?.razorpay_payment_id || '').trim();
  const txn = String(order?.upi_txn_id || '').trim();
  if (String(order?.razorpay_payment_id || '').startsWith('pay_')) return order.razorpay_payment_id;
  if (gw.startsWith('pay_')) return gw;
  if (txn.startsWith('pay_')) return txn;
  return '';
}

export const STATUS_META = {
  payment_pending:  { icon: '💳', label: 'Payment Pending',  labelTe: 'చెల్లింపు పెండింగ్‌లో ఉంది', color: '#92400E', bg: '#FEF3C7' },
  pending:          { icon: '⏳', label: 'Order Placed',    labelTe: 'ఆర్డర్ చేయబడింది',      color: '#B8873B', bg: '#F3E7D2' },
  confirmed:        { icon: '✅', label: 'Confirmed',        labelTe: 'నిర్ధారించబడింది',       color: '#1565c0', bg: '#e3f2fd' },
  dispatched:       { icon: '🚚', label: 'Out for Delivery', labelTe: 'డెలివరీలో ఉంది',        color: '#6a1b9a', bg: '#f3e5f5' },
  delivered:        { icon: '🎉', label: 'Delivered',        labelTe: 'డెలివరీ అయింది',         color: '#1A3D16', bg: '#E9F2E4' },
  cancelled:        { icon: '✕',  label: 'Cancelled',        labelTe: 'రద్దు చేయబడింది',       color: '#c62828', bg: '#ffebee' },
  return_requested: { icon: '↩',  label: 'Return Requested', labelTe: 'రిటర్న్ అభ్యర్థించబడింది', color: '#6d4c41', bg: '#efebe9' },
  returned:         { icon: '✔',  label: 'Returned',         labelTe: 'రిటర్న్ అయింది',         color: '#37474f', bg: '#eceff1' },
};
